import { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from 'sonner'

import { router } from '@/app-router'
import AuthService from '@/services/auth.service'
import CompaniesService from '@/services/companies.service'
import { useAuthStore } from '@/stores/authStore'
import { useBrandingStore } from '@/stores/brandingStore'
import { useUIStore } from '@/stores/uiStore'

export default function App() {
  const restoreSession = useAuthStore((state) => state.restoreSession)
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const setUser = useAuthStore((state) => state.setUser)
  const logout = useAuthStore((state) => state.logout)
  const theme = useUIStore((state) => state.theme)
  const branding = useBrandingStore((state) => state)
  const setBranding = useBrandingStore((state) => state.setBranding)
  const resetBranding = useBrandingStore((state) => state.resetBranding)
  const hasPersistedBranding = branding.loaded && Boolean(branding.platform_name?.trim())

  useEffect(() => {
    restoreSession()
  }, [restoreSession])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  useEffect(() => {
    if (!isAuthenticated || user) return

    AuthService.me()
      .then((me) => {
        setUser(me)
      })
      .catch(() => {
        logout()
      })
  }, [isAuthenticated, logout, setUser, user])

  useEffect(() => {
    const loadBranding = async () => {
      if (isAuthenticated && user?.workspace_id) {
        try {
          const payload = await CompaniesService.getCurrentBranding()
          setBranding(payload)
          return
        } catch {
          // Fall back to public branding if the authenticated workspace call fails.
        }
      }

      if (hasPersistedBranding) {
        return
      }

      try {
        const payload = await CompaniesService.getPublicBranding(window.location.host)
        setBranding(payload)
      } catch {
        resetBranding()
      }
    }

    loadBranding().catch(() => {
      resetBranding()
    })
  }, [hasPersistedBranding, isAuthenticated, resetBranding, setBranding, user?.workspace_id])

  useEffect(() => {
    document.title = branding.platform_name || 'ISing'
  }, [branding.platform_name])

  return (
    <>
      <RouterProvider router={router} />
      <Toaster richColors position="top-right" />
    </>
  )
}
